import { HttpStatusCode } from "axios";
import { getApiErrorData, getApiErrorStatus } from "_shared/_errors/api-error-message";
import type Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import type Session from "users/__session/session";
import {
  type ProposalTemplateTeamChoices,
  type SaveProposalTemplatePayload,
  saveProposalTemplatePayload,
} from "work/proposal-templates/_api/save-proposal-template-api-request";
import { proposalTemplatePartyOf } from "work/proposal-templates/proposal-template";
import { REFUSED_DOCUMENT_IDS_FIELD } from "work/proposals/_views/drafts/document-refusal-message";
import { buildDraftFromPreviousProposal } from "work/proposals/drafts-from-previous/draft-from-previous-proposal";
import type ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import type Proposal from "work/proposals/proposal";

/** What the page action is called. Not "Save as Template", which each tab already uses for its own table. */
export const SAVE_AS_PROPOSAL_TEMPLATE_LABEL = "Save as Proposal Template";
/** The one-word caption under the archived header's icon, where the full label crowds a phone. */
export const SAVE_AS_PROPOSAL_TEMPLATE_CAPTION = "Template";
export const SAVE_AS_PROPOSAL_TEMPLATE_TOOLTIP =
  "Save your side of this proposal as a template: its details, your team, fees and documents, and the other " +
  "party's team if you choose. " +
  "Start a draft from it with Create from Template on the Proposals page.";

export type SaveProposalAsTemplateArgs = {
  name: string;
  /** Which teams to keep, as the dialog asked (#6637). Without it, the saver's own team alone. */
  teams?: ProposalTemplateTeamChoices;
  /** The saved proposal, never the reader's unsaved edits. */
  proposal: Proposal;
  session: Session;
  /** Who on the proposal's teams cannot be carried over, as the server answers it now. */
  fetchTeamReuse: (proposalId: Guid) => Promise<ProposalTeamReuse>;
  save: (payload: SaveProposalTemplatePayload) => Promise<unknown>;
};

/**
 * Saves the reader's side of `proposal` as a named template (#6129).
 *
 * The proposal is first carried over exactly as Create from Previous carries it into a new draft
 * (#1569), so the template holds what such a draft would: nothing the reader cannot see, and nobody
 * who has left. The server makes the template's own copy of each document.
 *
 * Resolves true once the template exists. Otherwise it has told the user why, and resolves false.
 */
export async function saveProposalAsTemplate(args: SaveProposalAsTemplateArgs): Promise<boolean> {
  const { name, teams, proposal, session, fetchTeamReuse, save } = args;
  if (!proposal.id) throw new Error("A saved proposal is required to save it as a template.");

  let payload: SaveProposalTemplatePayload;
  try {
    const teamReuse = await fetchTeamReuse(proposal.id);
    const draft = buildDraftFromPreviousProposal({
      proposal,
      session,
      teamReuse,
    });
    payload = saveProposalTemplatePayload({
      name,
      party: proposalTemplatePartyOf(session),
      source: proposal,
      carried: draft.builder.buildNewDraft(),
      teams,
    });
  } catch (error) {
    console.error("Failed to prepare a proposal template", error);
    enqueueSnackbar("Could not save this proposal as a template. Please try again.", { variant: "error" });
    return false;
  }

  try {
    await save(payload);
  } catch (error) {
    console.error("Failed to save a proposal template", error);
    enqueueSnackbar(templateRefusalMessage(error, name), { variant: "error" });
    return false;
  }

  enqueueSnackbar(`Saved the proposal template ${name}.`, {
    variant: "success",
  });
  return true;
}

/** The sentence for a save the server refused, saying what the user can do about it. */
export function templateRefusalMessage(error: unknown, name: string): string {
  switch (getApiErrorStatus(error)) {
    case HttpStatusCode.Conflict:
      return `You already have a proposal template called ${name}. Choose another name.`;
    case HttpStatusCode.Forbidden:
      return refusesDocuments(getApiErrorData(error))
        ? "You do not have access to one or more of this proposal's documents, so it cannot be saved as a template."
        : "You do not have permission to save proposal templates.";
    case HttpStatusCode.NotFound:
      return "One of this proposal's documents no longer exists, so it cannot be saved as a template.";
    case HttpStatusCode.BadRequest:
      return "This proposal cannot be saved as a template. Check its team and fees, then try again.";
    default:
      return "Could not save the template. Please try again.";
  }
}

function refusesDocuments(data: unknown): boolean {
  if (typeof data !== "object" || data === null || !("errors" in data)) return false;
  const errors = data.errors;
  return typeof errors === "object" && errors !== null && REFUSED_DOCUMENT_IDS_FIELD in errors;
}
